import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, Star, ChevronDown } from "lucide-react";
import { useLang } from "../../site-context";
import { LINKS } from "../../i18n";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 220]);

  return (
    <section id="inicio" ref={ref} className="relative overflow-hidden pt-32 lg:pt-40" data-testid="hero-section">
      <motion.div
        style={{ y: glowY }}
        className="pointer-events-none absolute -top-40 right-[-10%] h-[600px] w-[600px] rounded-full bg-[#e3a008]/10 blur-[140px]"
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#e3a008]/40 to-transparent"
        aria-hidden="true"
      />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2, ease: EASE }}
              className="ff-mono mb-6 text-[11px] uppercase tracking-[0.35em] text-[#e3a008] sm:text-xs"
              data-testid="hero-eyebrow"
            >
              {t.hero_eyebrow}
            </motion.p>

            <h1 className="ff-display text-5xl font-semibold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl" data-testid="hero-title">
              {t.hero_lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-1">
                  <motion.span
                    className={`block ${i === 2 ? "text-gold-gradient italic" : ""}`}
                    initial={{ y: "115%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 1, delay: 0.3 + i * 0.14, ease: EASE }}
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
              className="mt-6 max-w-xl text-base font-light leading-relaxed text-slate-300 sm:text-lg"
              data-testid="hero-subtitle"
            >
              {t.hero_sub}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <motion.a
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                href={LINKS.phone}
                data-testid="hero-call-cta-button"
                className="flex items-center gap-3 rounded-full bg-[#e3a008] px-7 py-4 text-base font-semibold text-[#0a1120] shadow-[0_0_36px_rgba(227,160,8,0.35)] transition-shadow duration-300 hover:shadow-[0_0_48px_rgba(227,160,8,0.55)]"
              >
                <Phone className="h-5 w-5" />
                {t.hero_cta_call}
              </motion.a>
              <motion.a
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.97 }}
                href={LINKS.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-cta-button"
                className="flex items-center gap-3 rounded-full border border-[#25D366]/50 bg-[#25D366]/10 px-7 py-4 text-base font-semibold text-[#4be385] transition-colors duration-300 hover:bg-[#25D366]/20"
              >
                <MessageCircle className="h-5 w-5" />
                {t.hero_cta_whatsapp}
              </motion.a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
            >
              {[
                { v: "50+", l: t.stat_years, testid: "hero-stat-years" },
                { v: "4", l: t.stat_cars, testid: "hero-stat-cars" },
                { v: "5.0", l: t.stat_rating, star: true, testid: "hero-stat-rating" },
              ].map((s) => (
                <div key={s.l} data-testid={s.testid}>
                  <div className="ff-display flex items-center gap-1.5 text-3xl font-semibold text-white">
                    {s.v}
                    {s.star && <Star className="h-5 w-5 fill-[#e3a008] text-[#e3a008]" />}
                  </div>
                  <div className="ff-mono mt-1 text-[10px] uppercase tracking-[0.25em] text-slate-500">
                    {s.l}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.6, ease: EASE }}
              className="relative"
            >
              <div className="absolute -inset-4 rounded-3xl bg-[#e3a008]/10 blur-2xl" aria-hidden="true" />
              <motion.div style={{ y: imgY }} className="gold-frame relative overflow-hidden rounded-2xl shadow-2xl shadow-black/60">
                <img
                  src="/assets/fleet-church.jpg"
                  alt="Frota de táxis Mercedes-Benz dos Táxis Leão & Henrique em Lamoso"
                  className="h-full w-full object-cover"
                  data-testid="hero-fleet-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a1120]/60 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 flex items-center gap-2 rounded-full border border-white/10 bg-[#0a1120]/70 px-4 py-2 backdrop-blur-md">
                  <img src="/assets/lion-mark.png" alt="" className="h-6 w-6 rounded-full" />
                  <span className="ff-mono text-[10px] uppercase tracking-[0.25em] text-[#e3a008]">
                    Lamoso · Est. 50+ anos
                  </span>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="flex justify-center py-14"
        >
          <div className="flex flex-col items-center gap-2 text-slate-500">
            <span className="ff-mono text-[10px] uppercase tracking-[0.3em]">{t.hero_scroll}</span>
            <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
              <ChevronDown className="h-4 w-4 text-[#e3a008]" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
